import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, Cell,
} from "recharts";
import * as XLSX from "xlsx";
import api from "../services/api";
import "./LaborHoursAIReport.css";

/* ───────────────────────────── 定数 ───────────────────────────── */
const PALETTE = ["#8b5cf6", "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#06b6d4", "#ec4899"];
const DEFAULT_PROMPT =
  "200時間以上の勤務時間がある従業員の残業傾向を分析してください。リスクの高い従業員へのフォローアップ提案も含めてください。";

/* ───────────────────────────── ユーティリティ ───────────────────────────── */
const buildMonthOptions = () => {
  const now = new Date();
  const options = [];
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = d.toISOString().slice(0, 7);
    options.push({ value: ym, label: `${d.getFullYear()}年${d.getMonth() + 1}月` });
  }
  return options;
};

const formatHours = (h) => (h != null ? `${h}h` : "—");

/* ───────────────────────────── サブコンポーネント ───────────────────────────── */
const KpiCard = ({ label, value, sub, color = "#6366f1", alert = false, icon }) => (
  <div className="ai-kpi-card" style={{ borderColor: alert ? "#fca5a5" : "#c4b5fd", background: alert ? "#fef2f2" : "#f5f3ff" }}>
    <div className="ai-kpi-icon">{icon}</div>
    <div className="ai-kpi-label">{label}</div>
    <div className="ai-kpi-value" style={{ color: alert ? "#dc2626" : color }}>{value}</div>
    {sub && <div className="ai-kpi-sub">{sub}</div>}
  </div>
);

const SectionTitle = ({ children, icon }) => (
  <h3 className="ai-section-title">{icon && <span>{icon}</span>} {children}</h3>
);

/* ───────────────────────────── メインコンポーネント ───────────────────────────── */
const LaborHoursAIReport = () => {
  const location = useLocation();
  const initDept = location.state?.departmentName ?? "";
  const initDate = location.state?.printEndDate ?? new Date().toISOString().slice(0, 7);
  const printRef = useRef(null);

  /* ─── フィルター状態 ─── */
  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const [targetMonth, setTargetMonth] = useState(initDate.slice(0, 7));
  const [selectedDept, setSelectedDept] = useState(initDept);
  const [period, setPeriod] = useState(13);
  const [customPrompt, setCustomPrompt] = useState(DEFAULT_PROMPT);
  const [departments, setDepartments] = useState([]);

  /* ─── データ状態 ─── */
  const [aiReport, setAiReport] = useState("");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [buRows, setBuRows] = useState([]);
  const [kaRows, setKaRows] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [top5Workers, setTop5Workers] = useState([]);
  const [suddenIncrease, setSuddenIncrease] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const [deptChartMode, setDeptChartMode] = useState("bu");

  /* ─── 部署一覧取得 ─── */
  useEffect(() => {
    api.get("/api/departments/").then((res) => {
      const flat = [];
      const walk = (nodes) => nodes.forEach((n) => { flat.push(n.departmentName); if (n.children) walk(n.children); });
      walk(res.data || []);
      setDepartments(flat.filter(Boolean));
    }).catch(() => {});
  }, []);

  /* ─── AI分析実行 ─── */
  const handleAnalyze = useCallback(async () => {
    setLoading(true);
    setError("");
    setHasAnalyzed(true);
    const prevMonth = (() => {
      const [y, m] = targetMonth.split("-").map(Number);
      const prev = new Date(y, m - 2, 1);
      return prev.toISOString().slice(0, 7);
    })();
    try {
      const res = await api.post("/api/labor-hours/ai-analyze/", {
        departmentName: selectedDept,
        target_months: [prevMonth, targetMonth],
        customPrompt,
        period,
      });
      setAiReport(res.data.report ?? "");
      setStats(res.data.stats ?? null);
      setRows(res.data.rows ?? []);
      setBuRows(res.data.bu_rows ?? []);
      setKaRows(res.data.ka_rows ?? []);
      setMonthlyTrend(res.data.monthly_trend ?? []);
      setTop5Workers(res.data.top5_workers ?? []);
      setSuddenIncrease(res.data.sudden_increase ?? []);
    } catch (err) {
      setError(err.response?.data?.error ?? "分析データの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, [targetMonth, selectedDept, customPrompt, period]);

  /* ─── 初回自動実行（location.stateがある場合） ─── */
  useEffect(() => {
    if (initDate && !hasAnalyzed) {
      handleAnalyze();
    }
  }, []); // 初回マウント時のみ実行

  /* ─── Excelエクスポート ─── */
  const handleExportExcel = () => {
    if (!stats) return;
    const wb = XLSX.utils.book_new();

    // KPIシート
    const kpiData = [
      ["指標", "値"],
      ["対象月", stats["対象月"]],
      ["集計社員数", stats["集計社員数"]],
      ["平均労働時間", `${stats["対象月の平均労働時間"]}h`],
      ["最大労働時間", `${stats["対象月の最大労働時間"]}h`],
      ["残業率", `${stats["残業率"]}%`],
      ["前月比差", stats["前月比差"]],
      ["前年同月比差", stats["前年同月比差"]],
      ["ハイリスク者数", `${stats["ハイリスク者数（右肩上がり）"]}名`],
      ["45h残業超過", `${stats["45h超過人数"]}名`],
      ["60h残業超過", `${stats["60h超過人数"]}名`],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(kpiData), "KPI");

    // 部署別シート
    if (buRows.length > 0) {
      const deptData = [["部署（部）", "平均労働時間(h)", "通常時間(h)", "残業時間(h)", "人数"]];
      buRows.forEach((r) => deptData.push([r.departmentBuName, r.averageHoursPrevMonth, r.normalHours, r.overtimeHours, r.headcount]));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(deptData), "部署別（部）");
    }
    if (kaRows.length > 0) {
      const kaData = [["課", "平均労働時間(h)", "通常時間(h)", "残業時間(h)", "人数"]];
      kaRows.forEach((r) => kaData.push([r.departmentBuName, r.averageHoursPrevMonth, r.normalHours, r.overtimeHours, r.headcount]));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(kaData), "課別");
    }
    if (rows.length > 0 && buRows.length === 0) {
      const deptData = [["部署", "平均労働時間(h)", "通常時間(h)", "残業時間(h)", "人数"]];
      rows.forEach((r) => deptData.push([r.departmentBuName, r.averageHoursPrevMonth, r.normalHours, r.overtimeHours, r.headcount]));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(deptData), "部署別");
    }

    // 月次トレンドシート
    if (monthlyTrend.length > 0) {
      const trendData = [["月", "総労働時間(h)", "平均労働時間(h)", "在籍人数"]];
      monthlyTrend.forEach((r) => trendData.push([r.month, r.totalHours, r.avgHours, r.headcount]));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(trendData), "月次トレンド");
    }

    // AIレポートシート
    if (aiReport) {
      const reportData = [["AIレポート"], [aiReport]];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(reportData), "AIレポート");
    }

    XLSX.writeFile(wb, `AI労務診断_${stats["対象月"]}_${selectedDept || "全社"}.xlsx`);
  };

  /* ─── チャートデータ ─── */
  const deptChartData = useMemo(() =>
    rows.filter((r) => r.averageHoursPrevMonth > 0).slice(0, 10),
    [rows]
  );

  const buChartData = useMemo(() =>
    buRows.filter((r) => r.averageHoursPrevMonth > 0).slice(0, 10),
    [buRows]
  );

  const kaChartData = useMemo(() =>
    kaRows.filter((r) => r.averageHoursPrevMonth > 0).slice(0, 15),
    [kaRows]
  );

  const activeDeptChart = deptChartMode === "bu" ? buChartData : kaChartData;
  const hasHierarchyData = buRows.length > 0 || kaRows.length > 0;

  const trendChartData = useMemo(() =>
    monthlyTrend.map((r) => ({ ...r, month: r.month.slice(5) + "月" })),
    [monthlyTrend]
  );

  const seasonalDiff = stats?.["前年同月比差"] ?? "";
  const isIncrease = seasonalDiff.startsWith("+") && seasonalDiff !== "+0.0h";

  return (
    <div className="ai-report-root" ref={printRef}>
      {/* ─────────── エリア1: コントロールパネル ─────────── */}
      <div className="ai-control-panel no-print">
        <div className="ai-control-header">
          <Link to="/admin/labor-hours-list" className="ai-back-link">← 一覧に戻る</Link>
          <h2 className="ai-page-title">🤖 AI労務診断レポート</h2>
          <div className="ai-export-buttons">
            <button className="ai-btn ai-btn-secondary" onClick={() => window.print()} disabled={!hasAnalyzed}>
              🖨 PDF印刷
            </button>
            <button className="ai-btn ai-btn-secondary" onClick={handleExportExcel} disabled={!stats}>
              📊 Excelエクスポート
            </button>
          </div>
        </div>

        <div className="ai-filters-row">
          {/* 対象月 */}
          <div className="ai-filter-group">
            <label className="ai-filter-label">対象月</label>
            <select className="ai-select" value={targetMonth} onChange={(e) => setTargetMonth(e.target.value)}>
              {monthOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* 部署 */}
          <div className="ai-filter-group">
            <label className="ai-filter-label">部署</label>
            <select className="ai-select" value={selectedDept} onChange={(e) => setSelectedDept(e.target.value)}>
              <option value="">全社</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>

          {/* 分析期間 */}
          <div className="ai-filter-group">
            <label className="ai-filter-label">分析期間</label>
            <select className="ai-select" value={period} onChange={(e) => setPeriod(Number(e.target.value))}>
              <option value={3}>直近3ヶ月</option>
              <option value={6}>直近6ヶ月</option>
              <option value={13}>直近13ヶ月</option>
            </select>
          </div>
        </div>

        {/* カスタムプロンプト */}
        <div className="ai-prompt-area">
          <label className="ai-filter-label">カスタム分析リクエスト</label>
          <textarea
            className="ai-textarea"
            rows={3}
            value={customPrompt}
            onChange={(e) => setCustomPrompt(e.target.value)}
            placeholder="例：チームAとチームBのパフォーマンスを比較してください。残業が多い部署への改善策を提案してください。"
          />
        </div>

        <div className="ai-analyze-btn-row">
          <button
            className="ai-btn ai-btn-primary"
            onClick={handleAnalyze}
            disabled={loading}
          >
            {loading ? "⏳ AI分析中..." : "🚀 AI分析を開始"}
          </button>
        </div>
      </div>

      {/* ─────────── 印刷ヘッダー ─────────── */}
      <div className="print-only ai-print-header">
        <h1>🤖 AI労務診断レポート</h1>
        <p>{stats?.["対象月"]} {selectedDept || "全社"} ／ 分析期間：直近{period}ヶ月</p>
      </div>

      {/* ─────────── ローディング / エラー ─────────── */}
      {loading && (
        <div className="ai-loading">
          <div className="ai-loading-spinner"></div>
          <p>AIが勤務データを解析中です...</p>
          <p className="ai-loading-sub">Geminiによる分析には30秒ほどかかる場合があります</p>
        </div>
      )}

      {!loading && error && (
        <div className="ai-error-box">⚠️ {error}</div>
      )}

      {!loading && !hasAnalyzed && !error && (
        <div className="ai-empty-state">
          <div className="ai-empty-icon">📊</div>
          <p>フィルターを設定して「AI分析を開始」をクリックしてください</p>
        </div>
      )}

      {/* ─────────── エリア2: KPIカード ─────────── */}
      {!loading && stats && (
        <div className="ai-kpi-section">
          <KpiCard icon="👥" label="総従業員数" value={`${stats["集計社員数"]}名`} sub={`${stats["対象月"]}のデータ`} />
          <KpiCard icon="⏱" label="平均労働時間" value={formatHours(stats["対象月の平均労働時間"])} sub={`最大 ${formatHours(stats["対象月の最大労働時間"])}`} color="#3b82f6" />
          <KpiCard icon="📈" label="残業率" value={`${stats["残業率"]}%`} sub="月160h基準" color="#f59e0b" alert={stats["残業率"] > 20} />
          <KpiCard icon="📅" label="前年同月比" value={seasonalDiff} sub={`前年同月 ${formatHours(stats["前年同月の平均労働時間"])}`} color={isIncrease ? "#dc2626" : "#16a34a"} alert={isIncrease} />
          <KpiCard icon="⚠️" label="45h残業超過" value={`${stats["45h超過人数"]}名`} sub="法定上限(月45h)" alert={stats["45h超過人数"] > 0} />
          <KpiCard icon="🚨" label="60h残業超過" value={`${stats["60h超過人数"]}名`} sub="特別条項(月60h)" alert={stats["60h超過人数"] > 0} />
          <KpiCard icon="🔴" label="ハイリスク者数" value={`${stats["ハイリスク者数（右肩上がり）"]}名`} sub="継続的増加傾向" alert={stats["ハイリスク者数（右肩上がり）"] > 0} />
        </div>
      )}

      {/* ─────────── エリア3: グラフ + AIレポート ─────────── */}
      {!loading && stats && (
        <div className="ai-main-content">
          {/* ─── 左カラム: グラフ群 ─── */}
          <div className="ai-charts-col">

            {/* 月次トレンド折れ線グラフ */}
            {trendChartData.length > 0 && (
              <div className="ai-chart-card">
                <SectionTitle icon="📉">月次労働時間トレンド（直近{period}ヶ月）</SectionTitle>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trendChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis unit="h" tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v, n) => [`${v}h`, n === "avgHours" ? "平均" : "合計"]} />
                    <Legend formatter={(v) => v === "avgHours" ? "平均労働時間" : "総労働時間"} />
                    <Line type="monotone" dataKey="avgHours" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* 部署別・課別積み上げ棒グラフ */}
            {(hasHierarchyData || deptChartData.length > 0) && (
              <div className="ai-chart-card">
                <div className="ai-dept-chart-header">
                  <SectionTitle icon="🏢">部署別労働時間内訳（通常 vs 残業）</SectionTitle>
                  {hasHierarchyData && (
                    <div className="ai-dept-mode-tabs">
                      <button
                        className={`ai-dept-mode-tab ${deptChartMode === "bu" ? "ai-dept-mode-tab--active" : ""}`}
                        onClick={() => setDeptChartMode("bu")}
                      >
                        部署別
                      </button>
                      <button
                        className={`ai-dept-mode-tab ${deptChartMode === "ka" ? "ai-dept-mode-tab--active" : ""}`}
                        onClick={() => setDeptChartMode("ka")}
                      >
                        課別
                      </button>
                    </div>
                  )}
                </div>

                {activeDeptChart.length > 0 ? (
                  <>
                    <div className="ai-dept-chart-meta">
                      {deptChartMode === "bu" ? "各部署の平均労働時間（h）" : "各課の平均労働時間（h）"}
                    </div>
                    <ResponsiveContainer width="100%" height={Math.max(200, activeDeptChart.length * 42 + 60)}>
                      <BarChart data={activeDeptChart} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" unit="h" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="departmentBuName" width={100} tick={{ fontSize: 11 }} />
                        <Tooltip
                          formatter={(v, n) => [`${v}h`, n === "normalHours" ? "通常時間" : "残業時間"]}
                          labelFormatter={(label) => `${deptChartMode === "bu" ? "部署" : "課"}: ${label}`}
                        />
                        <Legend formatter={(v) => v === "normalHours" ? "通常時間" : "残業時間"} />
                        <Bar dataKey="normalHours" stackId="a" fill="#6366f1" barSize={22} name="normalHours" />
                        <Bar dataKey="overtimeHours" stackId="a" fill="#ef4444" barSize={22} name="overtimeHours" />
                      </BarChart>
                    </ResponsiveContainer>

                    {/* 平均時間テーブル */}
                    <div className="ai-dept-avg-table-wrap">
                      <table className="ai-attention-table" style={{ width: "100%", marginTop: 12 }}>
                        <thead>
                          <tr>
                            <th>{deptChartMode === "bu" ? "部署" : "課"}</th>
                            <th>平均時間</th>
                            <th>通常(h)</th>
                            <th>残業(h)</th>
                            <th>人数</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeDeptChart.map((r, i) => (
                            <tr key={i}>
                              <td>{r.departmentBuName}</td>
                              <td><strong>{r.averageHoursPrevMonth}h</strong></td>
                              <td>{r.normalHours}h</td>
                              <td style={{ color: r.overtimeHours > 0 ? "#ef4444" : undefined }}>
                                {r.overtimeHours > 0 ? `${r.overtimeHours}h` : "—"}
                              </td>
                              <td>{r.headcount}名</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="ai-no-data">
                    {deptChartMode === "bu" ? "部署データがありません" : "課データがありません（部署階層が設定されていない場合は「部署別」タブをご確認ください）"}
                  </p>
                )}
              </div>
            )}

            {/* 注目リスト */}
            <div className="ai-chart-card">
              <SectionTitle icon="🎯">注目リスト</SectionTitle>
              <div className="ai-attention-grid">
                {/* 労働時間上位5名 */}
                <div className="ai-attention-block">
                  <h4 className="ai-attention-subtitle">労働時間上位5名</h4>
                  {top5Workers.length > 0 ? (
                    <table className="ai-attention-table">
                      <thead>
                        <tr><th>ID</th><th>労働時間</th></tr>
                      </thead>
                      <tbody>
                        {top5Workers.map((w, i) => (
                          <tr key={i}>
                            <td>{w.id}</td>
                            <td className={w.hours > 200 ? "ai-alert-text" : ""}>{w.hours}h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p className="ai-no-data">該当者なし</p>}
                </div>

                {/* 急激な増加 */}
                <div className="ai-attention-block">
                  <h4 className="ai-attention-subtitle">急激な増加（前月比+30%以上）</h4>
                  {suddenIncrease.length > 0 ? (
                    <table className="ai-attention-table">
                      <thead>
                        <tr><th>ID</th><th>今月</th><th>前月</th><th>増加率</th></tr>
                      </thead>
                      <tbody>
                        {suddenIncrease.map((w, i) => (
                          <tr key={i}>
                            <td>{w.id}</td>
                            <td className="ai-alert-text">{w.hours}h</td>
                            <td>{w.prevHours}h</td>
                            <td className="ai-alert-text">+{w.increaseRate}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : <p className="ai-no-data">急激な増加は検出されませんでした</p>}
                </div>
              </div>
            </div>

            {/* 異常値上位3名 */}
            {stats["異常値上位3名"]?.length > 0 && (
              <div className="ai-chart-card">
                <SectionTitle icon="⚠️">高時間労働者（平均+20h超・匿名）</SectionTitle>
                <table className="ai-attention-table" style={{ width: "100%" }}>
                  <thead>
                    <tr><th>ID（匿名）</th><th>労働時間</th></tr>
                  </thead>
                  <tbody>
                    {stats["異常値上位3名"].map((r, i) => (
                      <tr key={i}>
                        <td>{r.id}</td>
                        <td className="ai-alert-text">{r.hours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ─── 右カラム: AIレポート ─── */}
          <div className="ai-report-col">
            <div className="ai-report-card">
              <div className="ai-report-card-header">
                <span className="ai-report-badge">AI</span>
                <h3 className="ai-report-card-title">Gemini 労務診断レポート</h3>
              </div>
              <div className="ai-report-meta">
                {stats["対象月"]} ／ {selectedDept || "全社"} ／ 直近{period}ヶ月分析
              </div>
              <div className="ai-report-divider" />
              {aiReport ? (
                <div className="ai-report-body">{aiReport}</div>
              ) : (
                <p className="ai-no-data">AIレポートが取得できませんでした。</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LaborHoursAIReport;
